/** Common UI helpers: status / priority badges, KPI cards, page headers. */
import { ChevronRight } from "lucide-react";

const STATUS_COLORS = {
  Created: "bg-slate-50 text-slate-700 border-slate-200",
  Assigned: "bg-blue-50 text-blue-700 border-blue-200",
  Accepted: "bg-cyan-50 text-cyan-700 border-cyan-200",
  "In Progress": "bg-amber-50 text-amber-700 border-amber-200",
  Completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Awaiting Approval": "bg-violet-50 text-violet-700 border-violet-200",
  Approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Rejected: "bg-red-50 text-red-700 border-red-200",
  "Rework Required": "bg-orange-50 text-orange-700 border-orange-200",
  "Return Confirmation Pending": "bg-yellow-50 text-yellow-700 border-yellow-200",
  "Return Confirmed": "bg-emerald-50 text-emerald-700 border-emerald-200",
  Closed: "bg-slate-100 text-slate-600 border-slate-300",
  Delayed: "bg-red-50 text-red-700 border-red-200",
  Cancelled: "bg-slate-100 text-slate-500 border-slate-200",
  Reopened: "bg-blue-50 text-blue-700 border-blue-200",
};

export function StatusBadge({ status }) {
  const cls = STATUS_COLORS[status] || "bg-slate-50 text-slate-600 border-slate-200";
  return <span className={`badge-base ${cls}`} data-testid={`status-${(status || "").toLowerCase().replace(/\s+/g, "-")}`}>{status || "—"}</span>;
}

const PRIO_COLORS = {
  Critical: "bg-red-50 text-red-700 border-red-200",
  High: "bg-orange-50 text-orange-700 border-orange-200",
  Medium: "bg-blue-50 text-blue-700 border-blue-200",
  Low: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

export function PriorityBadge({ priority }) {
  const cls = PRIO_COLORS[priority] || "bg-slate-50 text-slate-600 border-slate-200";
  return <span className={`badge-base ${cls}`}>{priority || "—"}</span>;
}

export function KpiCard({ label, value, hint, accent, testId, icon: Icon }) {
  // Compact stat card matching the 3007Courier `.stat` look (tinted icon box).
  const a = accent || "";
  const tint = a.includes("emerald") ? "bg-emerald-50 text-emerald-600"
    : a.includes("red") ? "bg-red-50 text-red-600"
    : (a.includes("amber") || a.includes("yellow")) ? "bg-amber-50 text-amber-600"
    : a.includes("violet") ? "bg-violet-50 text-violet-600"
    : "bg-[#EAEEF6] text-[#334E7B]";
  return (
    <div className="erp-card p-3 hover:border-[#334E7B] hover:shadow-sm transition-all flex items-start justify-between gap-2.5" data-testid={testId}>
      <div className="min-w-0">
        <div className="text-[10.5px] font-semibold uppercase tracking-wider text-slate-500">{label}</div>
        <div className={`mt-0.5 text-[22px] leading-tight font-heading font-extrabold tracking-tight ${accent || "text-slate-900"}`}>{value ?? "—"}</div>
        {hint && <div className="text-[10px] text-slate-500 mt-0.5">{hint}</div>}
      </div>
      {Icon && (
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${tint}`}>
          <Icon size={16} strokeWidth={1.8} />
        </div>
      )}
    </div>
  );
}

export function PageHeader({ title, breadcrumbs = [], actions }) {
  return (
    <div className="flex items-end justify-between mb-6 gap-4">
      <div>
        {breadcrumbs.length > 0 && (
          <div className="flex items-center text-xs text-slate-500 mb-2">
            {breadcrumbs.map((b, i) => (
              <span key={i} className="flex items-center">
                {i > 0 && <ChevronRight size={12} className="mx-1 text-slate-400" />}
                <span className={i === breadcrumbs.length - 1 ? "text-slate-700 font-medium" : ""}>{b}</span>
              </span>
            ))}
          </div>
        )}
        <h1 className="text-2xl lg:text-[26px] font-heading font-bold tracking-tight text-slate-900" data-testid="page-title">{title}</h1>
      </div>
      {actions && <div className="flex items-center gap-2 flex-shrink-0">{actions}</div>}
    </div>
  );
}

export function EmptyState({ title = "No records", hint = "Nothing to show yet." }) {
  return (
    <div className="text-center py-16">
      <div className="text-slate-400 text-sm uppercase tracking-wider mb-2">{title}</div>
      <div className="text-slate-500 text-sm">{hint}</div>
    </div>
  );
}

export function fmtDate(s) {
  if (!s) return "—";
  try {
    const d = new Date(s);
    if (isNaN(d.getTime())) return s;
    return d.toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return s; }
}
